import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { useGetGoalHistoryQuery } from '__generated__/api-types-and-hooks'
import { getTenantId } from 'utils/getTenantId'
import { fetcher } from 'lib/api-client'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { GoalHistorySection } from './GoalHistorySection.component'

type Props = {
  isOpen: boolean
  goalId: string
  goalName: string
  onClose: () => void
}

type SectionMessages = {
  [sessionId: string]: any[]
}

type SectionCursors = {
  [sessionId: string]: { cursor: string | null; hasMore: boolean }
}

export const AppGoalHistoryModal: React.FC<Props> = ({ isOpen, goalId, goalName, onClose }) => {
  const tenantId = getTenantId()
  const user = useSelector((state: RootState) => state.user.user)

  // Store accumulated messages for each section
  const [accumulatedMessages, setAccumulatedMessages] = useState<SectionMessages>({})
  // Track pagination cursors for each section
  const [sectionCursors, setSectionCursors] = useState<SectionCursors>({})
  const sectionCursorsRef = useRef<SectionCursors>({})
  // Track which sections are currently loading more
  const [loadingMore, setLoadingMore] = useState<Set<string>>(new Set())
  const loadingMoreRef = useRef<Set<string>>(new Set())
  // Refs for scroll containers to maintain scroll position
  const scrollContainerRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())
  // Track which sections have been initially scrolled to bottom
  const initialScrollDone = useRef<Set<string>>(new Set())

  // Keep refs in sync with state
  useEffect(() => {
    sectionCursorsRef.current = sectionCursors
  }, [sectionCursors])

  useEffect(() => {
    loadingMoreRef.current = loadingMore
  }, [loadingMore])

  const { data, isLoading, isError, refetch } = useGetGoalHistoryQuery(
    {
      input: {
        goalId,
        tenantId,
      },
    },
    {
      enabled: isOpen && !!goalId,
    }
  )

  // Handle data updates separately to avoid closure issues
  useEffect(() => {
    const sections = data?.getGoalHistory?.sections
    if (!sections) return

    // Update accumulated messages using callback to avoid closure issues
    setAccumulatedMessages((prev) => {
      const updated = { ...prev }
      sections.forEach((section) => {
        if (section?.sessionId && !prev[section.sessionId]) {
          // Only initialize if we don't already have messages
          updated[section.sessionId] = section.messages || []
        }
      })
      return updated
    })

    // Update cursors - ALWAYS update to get latest pagination state
    const newCursors: SectionCursors = {}
    sections.forEach((section) => {
      if (section?.sessionId) {
        newCursors[section.sessionId] = {
          cursor: section.nextCursor || null,
          hasMore: section.hasMore || false,
        }
      }
    })

    if (Object.keys(newCursors).length > 0) {
      setSectionCursors((prev) => ({ ...prev, ...newCursors }))
    }
  }, [data])

  const userInitial = useMemo(() => {
    const firstName = user?.firstName || ''
    const lastName = user?.lastName || ''
    return (
      `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() ||
      user?.name?.charAt(0)?.toUpperCase() ||
      ''
    )
  }, [user])

  // Handler to load more messages for a specific section
  const handleLoadMore = useCallback(
    async (sessionId: string) => {
      // Use refs to get the latest values to avoid stale closures
      const cursorInfo = sectionCursorsRef.current[sessionId]
      const isLoading = loadingMoreRef.current.has(sessionId)

      if (!cursorInfo || !cursorInfo.cursor || !cursorInfo.hasMore) {
        return
      }

      // Prevent duplicate calls
      if (isLoading) {
        return
      }

      // Save current scroll position before loading
      const scrollContainer = scrollContainerRefs.current.get(sessionId)
      const previousScrollHeight = scrollContainer?.scrollHeight || 0
      const previousScrollTop = scrollContainer?.scrollTop || 0
      setLoadingMore((prev) => new Set(prev).add(sessionId))

      try {
        const query = `
        query getGoalHistory($input: GetGoalHistoryInput!) {
          getGoalHistory(input: $input) {
            goalId
            sections {
              sessionId
              title
              type
              createdAt
              messages {
                senderName
                senderRole
                avatar
                text
                createdAt
              }
              hasMore
              nextCursor
            }
          }
        }
      `

        const variables = {
          input: {
            goalId,
            tenantId,
            sectionsPagination: [
              {
                sessionId,
                cursor: cursorInfo.cursor,
              },
            ],
          },
        }

        // fetcher returns a function, so we need to call it
        const fetcherFn = fetcher(query, variables)
        const result: any = await fetcherFn()

        // Find the section with new messages
        const updatedSection = result?.getGoalHistory?.sections?.find(
          (s: any) => s?.sessionId === sessionId
        )

        if (updatedSection?.messages) {
          // PREPEND new (older) messages to the beginning of accumulated messages
          setAccumulatedMessages((prev) => ({
            ...prev,
            [sessionId]: [...(updatedSection.messages || []), ...(prev[sessionId] || [])],
          }))

          // Update cursor info
          setSectionCursors((prev) => ({
            ...prev,
            [sessionId]: {
              cursor: updatedSection.nextCursor || null,
              hasMore: updatedSection.hasMore || false,
            },
          }))

          // Restore scroll position after messages are prepended
          // Use setTimeout to wait for DOM update
          setTimeout(() => {
            if (scrollContainer) {
              const newScrollHeight = scrollContainer.scrollHeight
              const scrollHeightDifference = newScrollHeight - previousScrollHeight
              scrollContainer.scrollTop = previousScrollTop + scrollHeightDifference
            }
          }, 0)
        }
      } catch (error) {
        console.error('Failed to load more messages:', error)
      } finally {
        setLoadingMore((prev) => {
          const newSet = new Set(prev)
          newSet.delete(sessionId)
          return newSet
        })
      }
    },
    [goalId, tenantId]
  )

  // Reset state when modal closes or goalId changes
  useEffect(() => {
    if (!isOpen) {
      setAccumulatedMessages({})
      setSectionCursors({})
      setLoadingMore(new Set())
      scrollContainerRefs.current.clear()
      initialScrollDone.current.clear()
    }
  }, [isOpen, goalId])

  const sections = useMemo(
    () => data?.getGoalHistory?.sections || [],
    [data?.getGoalHistory?.sections]
  )

  // Create stable ref callback maps using useMemo
  const scrollContainerRefCallbacks = useMemo(() => {
    const callbacks = new Map<string, (el: HTMLDivElement | null) => void>()
    sections.forEach((section) => {
      const sessionId = section?.sessionId
      if (sessionId && !callbacks.has(sessionId)) {
        callbacks.set(sessionId, (el: HTMLDivElement | null) => {
          scrollContainerRefs.current.set(sessionId, el)
          // Mark as scrolled when ref is set
          if (el && !initialScrollDone.current.has(sessionId)) {
            setTimeout(() => {
              el.scrollTop = el.scrollHeight
              initialScrollDone.current.add(sessionId)
            }, 50)
          }
        })
      }
    })
    return callbacks
  }, [sections])

  if (!isOpen) return null

  return (
    <AppModalBox
      title={`Goal History`}
      onClose={onClose}
      backgroundColor="bg-white"
      childClass="p-0"
      width="w-[900px]"
      position="center"
      rounded="xl"
    >
      <div className="p-6 bg-app-grey-5">
        <div className="text-lg text-app-grey-100 mb-4 font-semibold">{goalName}</div>
        {isLoading && <div className="py-8 text-center">Loading history…</div>}
        {isError && (
          <div className="py-8 text-center">
            <div className="mb-3 text-red-500">Failed to load goal history.</div>
            <button className="btn btn-primary" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !isError && sections.length === 0 && (
          <div className="py-8 text-center text-app-grey-60">No history yet.</div>
        )}
        <div className="space-y-8 max-h-[70vh] overflow-y-auto pr-2">
          {sections.map((section, idx) => {
            const sessionId = section?.sessionId || ''
            // Messages come from backend in chronological order (oldest first)
            const messages = accumulatedMessages[sessionId] || section?.messages || []
            const cursorInfo = sectionCursors[sessionId]
            const hasMore = cursorInfo?.hasMore || section?.hasMore || false
            const isLoading = loadingMore.has(sessionId)

            return (
              <GoalHistorySection
                key={`${section?.title}-${idx}-${sessionId}`}
                section={section}
                messages={messages}
                hasMore={hasMore}
                isLoading={isLoading}
                sessionId={sessionId}
                userInitial={userInitial}
                user={user}
                onLoadMore={handleLoadMore}
                scrollContainerRef={scrollContainerRefCallbacks.get(sessionId) || (() => {})}
              />
            )
          })}
        </div>
      </div>
    </AppModalBox>
  )
}
